import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import RouteDetailsModal from './RouteDetailsModal';
import { FaCheckCircle, FaExclamationCircle, FaExclamationTriangle } from 'react-icons/fa';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styles from './RoutesProgress.module.css';

// ===== helpers: локальные даты без UTC-сдвига =====
const toLocalDateStr = (d) => {
  if (!(d instanceof Date) || isNaN(d)) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const parseLocalDate = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

const RoutesProgress = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // ===== init из URL (чтобы при возврате всё сохранилось) =====
  const [startDate, setStartDate] = useState(() => parseLocalDate(searchParams.get('from')) || new Date());
  const [endDate, setEndDate] = useState(() => parseLocalDate(searchParams.get('to')) || new Date());
  const [selectedDriver, setSelectedDriver] = useState(() => searchParams.get('driver') || '');
  const [selectedLoader, setSelectedLoader] = useState(() => searchParams.get('loader') || '');

  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [loaders, setLoaders] = useState([]);

  // Модалка пока остаётся в коде
  const [modalOpen, setModalOpen] = useState(false);
  const [activeRoute, setActiveRoute] = useState(null);
  const openModal = (route) => { setActiveRoute(route); setModalOpen(true); };
  const closeModal = () => setModalOpen(false);

  // ===== синхронизация состояний → URL (без засорения истории) =====
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.set('from', toLocalDateStr(startDate));
    params.set('to', toLocalDateStr(endDate));
    selectedDriver ? params.set('driver', selectedDriver) : params.delete('driver');
    selectedLoader ? params.set('loader', selectedLoader) : params.delete('loader');
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, selectedDriver, selectedLoader]);

  // ===== загрузка маршрутов по диапазону =====
  useEffect(() => {
    let mounted = true;
    const fetchRoutes = async () => {
      setLoading(true);
      try {
        const response = await api.get('/routes/by-date', {
          params: {
            startDate: toLocalDateStr(startDate),
            endDate: toLocalDateStr(endDate),
          }
        });
        if (mounted && response && Array.isArray(response)) {
          setRoutes(response);
          setDrivers([...new Set(response.map(r => r.driver).filter(Boolean))]);
          setLoaders([
            ...new Set(
              response
                .flatMap(r => [r.loader1, r.loader2])
                .filter(Boolean)
            )
          ]);
        }
      } catch (error) {
        console.error('Ошибка при загрузке маршрутов:', error);
      } finally {
        mounted && setLoading(false);
      }
    };
    fetchRoutes();
    return () => { mounted = false; };
  }, [startDate, endDate]);

  const getProgress = (receipts) => {
    if (!Array.isArray(receipts)) {
      return { delivered: 0, inProgress: 0, undelivered: 0, totalOrders: 0 };
    }
    const totalOrders = receipts.length;
    const delivered = receipts.filter(r => r.status === 'Закрыта').length;
    const inProgress = receipts.filter(r => r.status === 'Открыта').length;
    const undelivered = receipts.filter(r => r.status === 'Не доставлено').length;
    return { delivered, inProgress, undelivered, totalOrders };
  };

  const computeLastChange = (receipts) => {
    const dates = receipts
      .map(r => r.status_changed_at)
      .filter(Boolean)
      .map(d => new Date(d));
    if (!dates.length) return null;

    const latest = new Date(Math.max(...dates));
    const diffMs = Date.now() - latest.getTime();
    const hours = Math.floor(diffMs / 3600000);
    const mins = Math.floor((diffMs % 3600000) / 60000);
    return `${hours} ч ${mins} мин назад`;
  };

  const filteredRoutes = routes.filter(route => {
    const driverMatch = !selectedDriver || route.driver === selectedDriver;
    const loaderMatch = !selectedLoader || [route.loader1, route.loader2].includes(selectedLoader);
    return driverMatch && loaderMatch;
  });

  return (
    <div className={styles.routesProgressRoot}>
      <div className={styles.filtersContainer}>
        <div className={styles.filterItem}>
          <label>С:</label>
          <input
            type="date"
            value={toLocalDateStr(startDate)}
            onChange={e => setStartDate(parseLocalDate(e.target.value))}
          />
        </div>
        <div className={styles.filterItem}>
          <label>По:</label>
          <input
            type="date"
            value={toLocalDateStr(endDate)}
            onChange={e => setEndDate(parseLocalDate(e.target.value))}
          />
        </div>
        <div className={styles.filterItem}>
          <label>Водитель:</label>
          <select
            value={selectedDriver}
            onChange={e => setSelectedDriver(e.target.value)}
          >
            <option value="">Все водители</option>
            {drivers.map((d, i) => (
              <option key={i} value={d}>{d}</option>
            ))}
          </select>
        </div>
        <div className={styles.filterItem}>
          <label>Грузчик:</label>
          <select
            value={selectedLoader}
            onChange={e => setSelectedLoader(e.target.value)}
          >
            <option value="">Все грузчики</option>
            {loaders.map((l, i) => (
              <option key={i} value={l}>{l}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div>Загрузка...</div>
      ) : filteredRoutes.length === 0 ? (
        <div>Нет маршрутов.</div>
      ) : (
        <div className={styles.routesContainer}>
          {filteredRoutes.map(route => {
            const { delivered, inProgress, undelivered, totalOrders } = getProgress(route.receipts || []);
            const lastChange = computeLastChange(route.receipts || []);
            const statuses = (route.receipts || []).map(r => r.status);

            let StatusIcon = FaExclamationTriangle;
            let iconColor = 'gold';
            if (statuses.length && statuses.every(s => s === 'Открыта')) {
              StatusIcon = FaExclamationCircle;
              iconColor = 'red';
            } else if (statuses.length && statuses.every(s => s === 'Закрыта' || s === 'Не доставлено')) {
              StatusIcon = FaCheckCircle;
              iconColor = 'green';
            }

            return (
              <div
                key={route.id}
                className={styles.routeCard}
                onClick={() => navigate(`/manager/routesprogress/${route.id}`)}
              >
                <div className={styles.routeHeader}>
                  <span>{route.driver}</span>
                  <span>{route.loader1}</span>
                  <span>{route.loader2}</span>
                  <StatusIcon className={styles.statusIcon} color={iconColor} />
                </div>
                <div className={styles.routeHeader}>
                  <span>№{route.route_number}</span>
                  <span>{route.time_from}-{route.time_to}</span>
                  <span>{delivered}/{totalOrders} Доставлено</span>
                </div>
                {lastChange && (
                  <div className={styles.lastChange}>
                    Последнее изменение: {lastChange}
                  </div>
                )}
                <div className={styles.progressContainer}>
                  <div className={styles.progressBar}>
                    <div
                      className={styles.progressDelivered}
                      style={{ width: `${totalOrders ? (delivered / totalOrders) * 100 : 0}%` }}
                    />
                    <div
                      className={styles.progressInProgress}
                      style={{ width: `${totalOrders ? (inProgress / totalOrders) * 100 : 0}%` }}
                    />
                    <div
                      className={styles.progressUndelivered}
                      style={{ width: `${totalOrders ? (undelivered / totalOrders) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Модалка остаётся в коде (не открываем по клику на карточку, сейчас уходим на страницу детализации) */}
      <RouteDetailsModal
        isOpen={modalOpen}
        onClose={closeModal}
        route={activeRoute}
      />
    </div>
  );
};

export default RoutesProgress;
